import React, { useEffect, useRef } from "react";
import { Box, Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import ChatMessage from "./ChatMessage";
import { ChatMessageData } from "./types";

interface ChatMessageListProps {
  messages: ChatMessageData[];
  onUseImage: (messageId: string) => void;
  onUseAsReference: (messageId: string) => void;
  onClearHistory: () => void;
  disabled?: boolean;
}

const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  onUseImage,
  onUseAsReference,
  onClearHistory,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages are added or updated
  useEffect(() => {
    // Use a small delay to ensure images are rendered before scrolling
    const timeoutId = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [messages]);

  return (
    <ScrollContainer>
      {messages.length === 0 ? (
        <EmptyState>{t("imageGenerator.emptyStateMessage")}</EmptyState>
      ) : (
        <>
          {messages.map(message => (
            <ChatMessage
              key={message.id}
              message={message}
              onUseImage={onUseImage}
              onUseAsReference={onUseAsReference}
            />
          ))}
          <div ref={messagesEndRef} />
          <Box mt={2} display="flex" justifyContent="center">
            <Button size="small" variant="outlined" onClick={onClearHistory} disabled={disabled}>
              {t("imageGenerator.clearHistory")}
            </Button>
          </Box>
        </>
      )}
    </ScrollContainer>
  );
};

export default ChatMessageList;

const ScrollContainer = styled(Box)`
  max-height: 500px;
  overflow-y: auto;
  padding: 16px;
  background-color: #f8f8f8;
`;

const EmptyState = styled(Box)`
  text-align: center;
  color: #999;
  padding: 32px;
`;
